/**
 * Serialisation contract for the deck-builder substrate (ADR-0015 §16.1).
 *
 * Converts `Inventory` and `Combatant` to/from JSON-safe DTOs so they can ride
 * in a persistence BLOB (the same UTF-8-JSON pattern the chunk / tile-metadata
 * rows use; see persistence/sqljs/schema.sql). The only shapes that are not
 * natively JSON-safe are `Combatant.trapState` (a `Map`) and
 * `oncePerCombatFired` (a `Set`); everything else is plain data and round-trips
 * structurally.
 *
 * Pure functions: serialise produces a fresh JSON-safe value; deserialise
 * rebuilds the runtime shape (including the `Map` / `Set`) from it. Neither
 * mutates its input.
 */

import type { CardId, Combatant, Grid, Inventory, StatusApplication, TrapRuntime } from './types';

/** A grid is plain data; its DTO is structurally identical, carrying the grid's
 * own `capacity`. */
export type SerialisedGrid = {
  placements: Grid['placements'];
  capacity: { rows: number; cols: number };
};

/** Inventory is plain data; its DTO is structurally identical. Single loadout
 * grid since ADR-0022 — pre-ADR-0022 saves carry an extra `stash` field that
 * deserialise ignores (migration: stash cards dropped on load). */
export type SerialisedInventory = {
  loadout: SerialisedGrid;
};

export type SerialisedCombatant = {
  id: string;
  name: string;
  hp: number;
  maxHp: number;
  inventory: SerialisedInventory;
  dicePool: number[];
  basePoolSize: number;
  statusBag: StatusApplication[];
  block: number;
  /** `Map` flattened to id-keyed entries. */
  trapState: Array<{ cardId: CardId; runtime: TrapRuntime }>;
  /** Frozen-card `Map` flattened to id → turns-remaining entries (ADR-0015 §17). */
  frozenCards: Array<{ cardId: CardId; turns: number }>;
  /** `Set` flattened to a list. */
  oncePerCombatFired: CardId[];
  /** Per-turn fire cap `Set` flattened to a list (ADR-0015 §19). */
  oncePerTurnFired: CardId[];
};

export function serialiseInventory(inventory: Inventory): SerialisedInventory {
  return {
    loadout: serialiseGrid(inventory.loadout),
  };
}

export function deserialiseInventory(dto: SerialisedInventory): Inventory {
  return {
    loadout: deserialiseGrid(dto.loadout),
  };
}

function serialiseGrid(grid: Grid): SerialisedGrid {
  return {
    placements: grid.placements.map(clonePlacement),
    capacity: { rows: grid.capacity.rows, cols: grid.capacity.cols },
  };
}

function deserialiseGrid(dto: SerialisedGrid): Grid {
  return {
    placements: dto.placements.map(clonePlacement),
    capacity: { rows: dto.capacity.rows, cols: dto.capacity.cols },
  };
}

export function serialiseCombatant(combatant: Combatant): SerialisedCombatant {
  return {
    id: combatant.id,
    name: combatant.name,
    hp: combatant.hp,
    maxHp: combatant.maxHp,
    inventory: serialiseInventory(combatant.inventory),
    dicePool: [...combatant.dicePool],
    basePoolSize: combatant.basePoolSize,
    statusBag: combatant.statusBag.map((s) => ({ ...s })),
    block: combatant.block,
    trapState: [...combatant.trapState.entries()].map(([cardId, runtime]) => ({
      cardId,
      runtime: { ...runtime },
    })),
    frozenCards: [...combatant.frozenCards.entries()].map(([cardId, turns]) => ({ cardId, turns })),
    oncePerCombatFired: [...combatant.oncePerCombatFired],
    oncePerTurnFired: [...combatant.oncePerTurnFired],
  };
}

export function deserialiseCombatant(dto: SerialisedCombatant): Combatant {
  return {
    id: dto.id,
    name: dto.name,
    hp: dto.hp,
    maxHp: dto.maxHp,
    inventory: deserialiseInventory(dto.inventory),
    dicePool: [...dto.dicePool] as Combatant['dicePool'],
    basePoolSize: dto.basePoolSize,
    statusBag: dto.statusBag.map((s) => ({ ...s })),
    block: dto.block,
    trapState: new Map(dto.trapState.map((e) => [e.cardId, { ...e.runtime }])),
    frozenCards: new Map((dto.frozenCards ?? []).map((e) => [e.cardId, e.turns])),
    oncePerCombatFired: new Set(dto.oncePerCombatFired),
    oncePerTurnFired: new Set(dto.oncePerTurnFired ?? []),
  };
}

/** Deep clone of a placement. Placements are pure data (no functions, no
 * cyclic refs), so a JSON round-trip is the simplest correct deep copy and
 * keeps serialise / deserialise free of shared references with their input. */
function clonePlacement<T extends Grid['placements'][number]>(placement: T): T {
  return JSON.parse(JSON.stringify(placement)) as T;
}
